import { FormatPageStructureEditor, IEditableFormatObject, IFormatObjectEditable } from "../editableFormat";
import { instanceOfIQuestionnairePropertyEditable } from "../editableFormat/properties";
import { ObjectUtility } from "../../../base/utilities";
import { instanceOfIQuestionnaireSpecifiable, IQuestionnaire } from "../../../documaker/domain/format/objects";

export class QuestionnaireGuidTree<T extends IFormatObjectEditable | IEditableFormatObject> {
  constructor(object: T) {
    this.guid = QuestionnaireGuidTree.toQuestionnaireGuid(object);
    this.childGuids = QuestionnaireGuidTree.toQuestionnaireChildGuids(object);
    this.source = object;
  }
  level: number = 0;
  parentGuid: string = null;
  guid: string = null;
  childGuids: string[] = [];
  parent: QuestionnaireGuidTree<T> = null;
  source: T = null;
  children: QuestionnaireGuidTree<T>[] = [];

  containParent(guid: string): boolean {
    if (!this.parent) {
      return false;
    } else if (this.parentGuid === guid) {
      return true;
    } else {
      return this.parent.containParent(guid);
    }
  }

  containChildren(guid: string): boolean {
    return this.children.some(c => c.guid === guid || c.containChildren(guid));
  }

  toChildren(): QuestionnaireGuidTree<T>[] {
    return this.children.reduce((p, c) => p.concat([c], (c.children.length === 0) ? [] : c.toChildren()), [] as QuestionnaireGuidTree<T>[]);
  }

  updateChildren(childGuids: string[], items: QuestionnaireGuidTree<T>[]) {
    this.children.forEach(c => {
      c.updateLevel(0);
      c.parentGuid = null;
      c.parent = null;
    });
    this.childGuids = childGuids;
    this.children = QuestionnaireGuidTree.toQuestionnaireGuidTreeChildren(this, items);
  }

  private updateLevel(level: number) {
    this.level = level;
    this.children.forEach(c => c.updateLevel(level + 1));
  }

  static from<T extends IFormatObjectEditable | IEditableFormatObject>(objects: T[]): QuestionnaireGuidTree<T>[] {
    const items = objects.map(o => new QuestionnaireGuidTree(o));
    items.forEach(v => v.children = QuestionnaireGuidTree.toQuestionnaireGuidTreeChildren(v, items));
    return items;
  }

  static sort<T extends IFormatObjectEditable | IEditableFormatObject>(items: QuestionnaireGuidTree<T>[]): QuestionnaireGuidTree<T>[] {
    const results: QuestionnaireGuidTree<T>[] = [];
    this.sortByGuidTree(results, items.filter(v => !v.parentGuid));
    return results;
  }

  private static sortByGuidTree<T extends IFormatObjectEditable | IEditableFormatObject>(results: QuestionnaireGuidTree<T>[], targets: QuestionnaireGuidTree<T>[]) {
    targets.forEach(v => {
      results.push(v);
      this.sortByGuidTree(results, v.children);
    });
  }

  private static toQuestionnaireGuid<T>(object: T): string {
    return instanceOfIQuestionnairePropertyEditable(object)
      ? object.questionnaire().getValue()._guid
      : instanceOfIQuestionnaireSpecifiable(object)
        ? object.questionnaire._guid
        : null;
  }

  private static toQuestionnaireChildGuids<T>(object: T): string[] {
    return instanceOfIQuestionnairePropertyEditable(object)
      ? object.questionnaire().getValue()._childGuids
      : instanceOfIQuestionnaireSpecifiable(object)
        ? object.questionnaire._childGuids
        : [];
  }

  private static toQuestionnaireGuidTreeChildren<T extends IFormatObjectEditable | IEditableFormatObject>(source: QuestionnaireGuidTree<T>, items: QuestionnaireGuidTree<T>[]): QuestionnaireGuidTree<T>[] {
    return source.childGuids.map(g => {
      const item = items.find(q => q.guid === g);
      if (!item || item.containParent(g)) {
        return null;
      }
      item.level = source.level + 1;
      item.parentGuid = source.guid;
      item.parent = source;
      item.children = this.toQuestionnaireGuidTreeChildren(item, items);
      return item;
    }).filter(q => !!q);
  }
}

export class QuestionnaireGuidTreeEditor {
  constructor(private source: FormatPageStructureEditor) {
    this.objects = source.getObjects();
    this.items = QuestionnaireGuidTree.from(this.objects);
  }
  objects: IFormatObjectEditable[] = [];
  items: QuestionnaireGuidTree<IFormatObjectEditable>[] = [];

  private selectedObjects(): IFormatObjectEditable[] {
    return this.objects.filter(o => o.isSelected());
  }

  private unselectedObjects(): IFormatObjectEditable[] {
    return this.objects.filter(o => !o.isSelected());
  }

  private selectedGuidTreeItems(): QuestionnaireGuidTree<IFormatObjectEditable>[] {
    return this.items.filter(a => a.source.isSelected());
  }

  private unselectedGuidTreeItems(): QuestionnaireGuidTree<IFormatObjectEditable>[] {
    return this.items.filter(a => !a.source.isSelected());
  }

  insertSelectedObjects(isHitObject: boolean, indexInUnselected: number) {
    this.removeUnnecessaryQuestionnaireChildGuids();
    const selectedGuidTreeItems = this.selectedGuidTreeItems().filter(s => !s.parent);
    if (isHitObject) {
      const prevToInsertGuidTreeItemFromUnselected = this.toPrevToInsertGuidTreeItem(indexInUnselected, this.unselectedGuidTreeItems());
      const prevToInsertGuidTreeItem = this.items.find(a => a === prevToInsertGuidTreeItemFromUnselected);
      const childrenLength = prevToInsertGuidTreeItem.toChildren().length;
      this.updateQuestionnaireChildGuids(prevToInsertGuidTreeItemFromUnselected, prevToInsertGuidTreeItem.children.concat(selectedGuidTreeItems));
      this.sortObjects(indexInUnselected + childrenLength);
    } else {
      const parentGuidTreeItem = this.toParentGuidTreeItem(indexInUnselected, this.unselectedGuidTreeItems());
      if (parentGuidTreeItem && !parentGuidTreeItem.isLastChild) {
        const prev = parentGuidTreeItem.parent.children.slice(0, parentGuidTreeItem.childIndex);
        const next = parentGuidTreeItem.parent.children.slice(parentGuidTreeItem.childIndex, parentGuidTreeItem.parent.children.length);
        this.updateQuestionnaireChildGuids(parentGuidTreeItem.parent, prev.concat(selectedGuidTreeItems, next));
      }
      this.sortObjects(indexInUnselected);
    }
  }

  toParentGuidTreeItem(index: number, items: QuestionnaireGuidTree<IFormatObjectEditable>[]): { parent: QuestionnaireGuidTree<IFormatObjectEditable>, childIndex: number, isLastChild: boolean } {
    const prevItem = this.toPrevToInsertGuidTreeItem(index, items);
    if (!prevItem) {
      return null;
    }
    return (prevItem.children.length === 0)
      ? this.toAncestorGuidTreeItem(prevItem)
      : { parent: prevItem, childIndex: 0, isLastChild: false };
  }

  private toPrevToInsertGuidTreeItem(index: number, items: QuestionnaireGuidTree<IFormatObjectEditable>[]): QuestionnaireGuidTree<IFormatObjectEditable> {
    return (0 < index && index <= items.length)
      ? items[index - 1]
      : null;
  }

  private toAncestorGuidTreeItem(item: QuestionnaireGuidTree<IFormatObjectEditable>): { parent: QuestionnaireGuidTree<IFormatObjectEditable>, childIndex: number, isLastChild: boolean } {
    if (!item.parent) {
      return null;
    }
    const parent = item.parent;
    const childIndex = parent.children.indexOf(item) + 1;
    const isLastChild = parent.children.length === childIndex;
    if (isLastChild) {
      const ancestor = this.toAncestorGuidTreeItem(parent);
      if (ancestor) {
        return ancestor;
      }
    }
    return { parent, childIndex, isLastChild };
  }

  private removeUnnecessaryQuestionnaireChildGuids() {
    // 選択したオブジェクトに含まれている不要な子アイテムを削除
    const selectedGuids = this.selectedGuidTreeItems().map(s => s.guid);
    this.items.forEach(i => {
      if (i.childGuids.length === 0 || selectedGuids.some(g => g === i.guid)) {
        return;
      }
      const childGuidItems = i.children.filter(c => selectedGuids.every(g => g !== c.guid));
      if (childGuidItems.length < i.children.length) {
        this.updateQuestionnaireChildGuids(i, childGuidItems);
      }
    });
  }

  private updateQuestionnaireChildGuids(item: QuestionnaireGuidTree<IFormatObjectEditable>, childGuidItems: QuestionnaireGuidTree<IFormatObjectEditable>[]) {
    if (!instanceOfIQuestionnairePropertyEditable(item.source)) {
      return;
    }
    const clone = ObjectUtility.copyDeeply(item.source.questionnaire().getValue()) as IQuestionnaire;
    const childGuids = childGuidItems.map(o => o.guid).filter(o => !!o);
    clone._childGuids = childGuids;
    item.source.questionnaire().setValue(clone);
    this.source.updateObject(item.source);
    // 管理ツリーを更新
    item.updateChildren(childGuids, this.items);
  }

  private sortObjects(indexInUnselected: number) {
    const sortedObjects = this.toSortedObjects(indexInUnselected);
    this.source.sortObjects((a, b) => sortedObjects.map(o => o.getSource()).indexOf(a) - sortedObjects.map(o => o.getSource()).indexOf(b));
  }

  private toSortedObjects(indexInUnselected: number): IFormatObjectEditable[] {
    // 選択したオブジェクトを挿入先に連続して並べて配置した結果を返す
    const selectedObjects = this.selectedObjects();
    const unselectedObjects = this.unselectedObjects();
    const nextToInsertObject = (indexInUnselected < unselectedObjects.length)
      ? unselectedObjects[indexInUnselected]
      : null;
    const edittingObjects = this.objects.concat();
    selectedObjects.forEach(o => {
      // オフセット算出
      const srcIndex = edittingObjects.indexOf(o);
      const dstIndex = nextToInsertObject
        ? edittingObjects.indexOf(nextToInsertObject)
        : unselectedObjects.length + selectedObjects.length;
      const isPositive = (dstIndex - srcIndex) < 0;
      const offset = dstIndex - srcIndex + (isPositive ? 0 : -1);
      // 入れ替え
      const from = srcIndex;
      const to = from + offset;
      const moved = edittingObjects[from];
      edittingObjects.splice(from, 1);
      edittingObjects.splice(to, 0, moved);
    });
    return edittingObjects;
  }
}
